/**
 * Naming and remembering the things on a page that scroll.
 *
 * WHY THIS IS A SEPARATE FILE WITH NO REACT IN IT
 *
 *   ScrollMemory needed a browser to run, so it was never tested, so the
 *   sentence in its header - "the whole app scrolls the window, so only
 *   window scroll is tracked" - went eighteen versions without anyone
 *   checking it. Everything here is a pure function over plain values and is
 *   covered by `node --test`. The component keeps only the wiring.
 *
 * WHAT WAS WRONG (27 September 2026, found by the Tradie's Bay chat)
 *
 *   ListDetailPage - the shopping list itself, the busiest screen in the app
 *   - has a root of `fixed inset-0 flex flex-col` and puts its entire body in
 *   a `flex-1 overflow-y-auto` child. Inside a page shaped like that,
 *   `window.scrollY` is 0 and stays 0 however far you scroll.
 *
 *   So ScrollMemory recorded 0, restored 0, and reported nothing. Scroll to
 *   the fortieth item, open something, come back: top of the list. Every
 *   time. The pages it did work on were the short ones nobody minded.
 *
 *   The sentence was true of the pages its author was looking at. That is the
 *   shape: a comment asserting something about the whole app, written from
 *   the part in view. Same as a rule that enumerates spellings, or node
 *   types, or one of the two ways a page can scroll.
 */

/**
 * A stable name for one scrolling element, as a path of steps down from
 * <body>: "DIV:1/MAIN:0/DIV:3".
 *
 * THE SEPARATOR IS LOAD-BEARING. Tradie's Bay wrote theirs as tag + index
 * with nothing between - "DIV3" - and read the index back off the end with a
 * regex. Correct for every tag that does not end in a digit, which is most
 * of them. An H1 at index 3 becomes "H13", which reads back as an H at index
 * 13, so the pane is never found and the position is dropped in silence.
 * Headings are on nearly every screen.
 *
 * Returns null for anything not attached to a document body.
 */
export function panePath(el, body) {
  if (!el || !body) return null;
  const steps = [];
  let node = el;
  while (node && node !== body) {
    const parent = node.parentNode;
    if (!parent) return null;              // detached: no stable name
    const index = Array.prototype.indexOf.call(parent.children, node);
    if (index < 0) return null;
    steps.push(`${node.tagName}:${index}`);
    node = parent;
  }
  if (node !== body) return null;          // not under this body at all
  return steps.reverse().join("/");
}

/** Walk a path back to the element it names, or null if the tree changed. */
export function paneAt(path, body) {
  if (!path || !body) return null;
  let node = body;
  for (const step of path.split("/")) {
    const cut = step.lastIndexOf(":");     // lastIndexOf, for the same reason
    if (cut < 0) return null;
    const tag = step.slice(0, cut);
    const index = Number(step.slice(cut + 1));
    if (!Number.isInteger(index) || index < 0) return null;
    const child = node.children && node.children[index];
    if (!child || child.tagName !== tag) return null;
    node = child;
  }
  return node;
}

/** The special name for the window itself, so one map holds both kinds. */
export const WINDOW_PANE = "@window";

/**
 * Should this recorded position be written down?
 *
 * A zero is not worth a row - it is the default, and storing it means a page
 * that legitimately scrolls the window cannot tell "never scrolled" from
 * "scrolled back to the top", which is the confusion that hid the bug.
 */
export function worthRecording(top) {
  return Number.isFinite(top) && top > 0;
}

/**
 * Is the page tall enough yet to put the scroll back?
 *
 * Pages fetch after mount and grow over several frames, so restoring
 * immediately lands short. Returns true once the target is reachable, or
 * once we have waited long enough that settling for the bottom is better
 * than another frame of nothing.
 */
export function readyToRestore({ target, maxScroll, tries, maxTries = 90 }) {
  if (!Number.isFinite(target) || target <= 0) return true;
  return maxScroll >= target - 2 || tries >= maxTries;
}

/** Where to actually land, given how far the pane can scroll right now. */
export function landingPosition(target, maxScroll) {
  const reachable = Math.max(0, Number.isFinite(maxScroll) ? maxScroll : 0);
  if (!Number.isFinite(target) || target <= 0) return 0;
  return Math.max(0, Math.min(target, reachable));
}

/**
 * Keep the map from growing without bound. Oldest entries go first; a
 * Map iterates in insertion order, so the first key is the oldest.
 */
export function trim(map, maxEntries) {
  while (map.size > maxEntries) {
    const oldest = map.keys().next().value;
    if (oldest === undefined) break;
    map.delete(oldest);
  }
  return map;
}
